import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router';
import { Banner, useApiClient, useQuery } from '@acc/web-shared';
import { QUOTE_TYPES, type QuoteType } from '@acc/domain';
import { PageHead, dateTime, useAgency } from '../agency';
import { ACCEPT, DocumentsPanel, checkFiles, uploadAll } from '../documents';
import { StatusText, type Opportunity } from './Quotes';

export interface IntakeItem {
  intakeId: string;
  customerName: string;
  type: QuoteType;
  phone: string | null;
  email: string | null;
  address: string | null;
  currentInsurance: string | null;
  notes: string | null;
  submittedBy: string;
  submittedAt: string;
  stage: 'Unassigned' | 'Assigned';
  opportunityId: string | null;
  assignedAt: string | null;
}

/** Management archives a waiting request (prototype deleteIntake); it can be restored from the Archive. */
function ArchiveIntake({ item, onDone }: { item: IntakeItem; onDone: () => void }) {
  const a = useAgency();
  const api = useApiClient();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const [busy, setBusy] = useState(false);
  async function archive() {
    if (!window.confirm(`Archive the request for ${item.customerName}? You can restore it from the Archive.`)) return;
    setBusy(true);
    try {
      await api(a.path(`/intake/${item.intakeId}/archive`), { method: 'POST' });
      a.notify('ok', `Request for ${item.customerName} archived.`);
      refreshCounts(); onDone();
    } catch (err) { a.notify('bad', (err as Error).message); } finally { setBusy(false); }
  }
  return <button type="button" className="btn small danger" disabled={busy} onClick={() => void archive()} aria-label={`Archive request for ${item.customerName}`}>Archive</button>;
}

/** An employee takes an unassigned request for themselves (baseline 9.3); whoever is first wins. */
function Claim({ item }: { item: IntakeItem }) {
  const a = useAgency();
  const api = useApiClient();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  async function claim() {
    setBusy(true);
    try {
      const o = await api<{ opportunityId: string }>(a.path(`/intake/${item.intakeId}/assign`), { method: 'POST', body: {} });
      a.notify('ok', `${item.customerName} is now assigned to you.`);
      nav(`${a.base}/quotes/${o.opportunityId}`);
    } catch (err) { a.notify('bad', (err as Error).message); setBusy(false); }
  }
  return <button type="button" className="btn small primary" disabled={busy} onClick={() => void claim()}>{busy ? 'Claiming…' : 'Assign to me'}</button>;
}

export function IntakeList() {
  const a = useAgency();
  const [params, setParams] = useSearchParams();
  const stage = params.get('stage') === 'Assigned' ? 'Assigned' : 'Unassigned';
  const [search, setSearch] = useState('');
  const q = useQuery<{ intake: IntakeItem[] }>(a.path('/intake'));
  // Both endpoints enforce agency scoping and per-employee quote visibility.
  // Never infer status or policy information from the intake record alone.
  const quotes = useQuery<{ opportunities: Opportunity[] }>(a.path('/opportunities?view=all'));
  const byId = new Map((quotes.data?.opportunities ?? []).map((o) => [o.opportunityId, o]));
  const waiting = (q.data?.intake ?? []).filter((i) => i.stage === 'Unassigned');
  const missed = (q.data?.intake ?? [])
    .filter((i) => i.stage === 'Assigned' && i.opportunityId)
    .map((i) => ({ intake: i, quote: byId.get(i.opportunityId!) }))
    .filter((r): r is { intake: IntakeItem; quote: Opportunity } =>
      !!r.quote && (r.quote.pcStatus === 'Missed' || r.quote.lifeStatus === 'Missed'))
    .filter(({ intake, quote }) =>
      !search.trim() || [intake.customerName, quote.policyNumber ?? '']
        .some((v) => v.toLowerCase().includes(search.trim().toLowerCase())))
    .sort((a, b) => (b.intake.assignedAt ?? b.intake.submittedAt).localeCompare(a.intake.assignedAt ?? a.intake.submittedAt));
  const years = [...new Set(missed.map(({ intake }) => (intake.assignedAt ?? intake.submittedAt).slice(0, 4)))];
  const loading = q.loading || (stage === 'Assigned' && quotes.loading);
  return (
    <>
      <PageHead title="Quote intake" sub="Awaiting assignment and missed-coverage assignment history.">
        <Link className="btn primary" to={`${a.base}/intake/new`}>New quote request</Link>
      </PageHead>
      <div className="tabs" role="tablist">
        {(['Unassigned', 'Assigned'] as const).map((s) => (
          <button key={s} type="button" role="tab" aria-selected={stage === s} className={stage === s ? 'on' : ''} onClick={() => setParams({ stage: s })}>{s === 'Unassigned' ? 'Awaiting assignment' : 'Assignment history (Missed)'}</button>
        ))}
      </div>
      {stage === 'Assigned' && <div className="toolbar">
        <input type="search" aria-label="Search assignment history by customer or policy number" placeholder="Search customer or policy number" value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && <button className="btn" type="button" onClick={() => setSearch('')}>Clear</button>}
      </div>}
      {q.error && <Banner tone="bad">{q.error.message}</Banner>}
      {stage === 'Assigned' && quotes.error && <Banner tone="bad">{quotes.error.message}</Banner>}
      {loading && <div className="card"><p className="muted">Loading…</p></div>}
      {!loading && !q.error && (stage === 'Unassigned' ? (
        <div className="card">
          {waiting.length === 0 ? <div className="empty">Nothing waiting. New requests appear here.</div> : (
            <table className="data">
              <thead><tr><th>Customer</th><th>Type</th><th>Submitted</th><th>By</th><th /></tr></thead>
              <tbody>{waiting.map((i) => (
                <tr key={i.intakeId}>
                  <td><strong>{i.customerName}</strong>{i.notes && <div className="muted" style={{ fontSize: 13 }}>{i.notes.slice(0, 90)}</div>}</td>
                  <td>{i.type}</td><td className="muted">{dateTime(i.submittedAt)}</td><td className="muted">{a.name(i.submittedBy)}</td>
                  <td style={{ textAlign: 'right' }}>{a.manager
                    ? <><Link className="btn small primary" to={`${a.base}/intake/${i.intakeId}/assign`}>Review / assign</Link> <ArchiveIntake item={i} onDone={q.reload} /></>
                    : <Claim item={i} />}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      ) : !quotes.error && (
        years.length === 0 ? <div className="card"><div className="empty">No assigned quotes with Missed P/C or Life status match.</div></div> :
        years.map((year) => (
          <section className="card" key={year} aria-label={`Assignment history ${year}`}>
            <h2>{year}</h2>
            <table className="data">
              <thead><tr><th>Customer</th><th>Policy #</th><th>Type</th><th>P/C</th><th>Life</th><th>Assigned</th><th /></tr></thead>
              <tbody>{missed.filter(({ intake }) => (intake.assignedAt ?? intake.submittedAt).startsWith(year)).map(({ intake, quote }) => (
                <tr key={intake.intakeId}>
                  <td><strong>{intake.customerName}</strong></td>
                  <td>{quote.policyNumber ?? '—'}</td>
                  <td>{intake.type}</td>
                  <td><StatusText s={quote.pcStatus} /></td>
                  <td><StatusText s={quote.lifeStatus} /></td>
                  <td className="muted">{dateTime(intake.assignedAt ?? intake.submittedAt)}</td>
                  <td><Link className="btn small" to={`${a.base}/quotes/${quote.opportunityId}`}>Open quote / reopen</Link></td>
                </tr>
              ))}</tbody>
            </table>
          </section>
        ))
      ))}
    </>
  );
}

export function IntakeNew() {
  const a = useAgency();
  const api = useApiClient();
  const nav = useNavigate();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const [f, setF] = useState({ customerName: '', type: 'New' as QuoteType, phone: '', email: '', address: '', currentInsurance: '', notes: '' });
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problem = checkFiles(files);
    if (problem) { setError(problem); return; }
    setBusy(true); setError('');
    try {
      const created = await api<{ intakeId: string }>(a.path('/intake'), { method: 'POST', body: f });
      const failed = await uploadAll(api, a.path(`/intake/${created.intakeId}/documents`), files);
      refreshCounts();
      if (failed.length) a.notify('bad', `Request submitted, but some documents were not attached — ${failed.join('; ')}`);
      else a.notify('ok', `Quote request for ${f.customerName} submitted${files.length ? ` with ${files.length} document${files.length > 1 ? 's' : ''}` : ''}.`);
      nav(`${a.base}/intake`);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  return (
    <>
      <div className="crumbs"><Link to={`${a.base}/intake`}>Quote intake</Link> / New request</div>
      <PageHead title="New quote request" sub="Fictional data only until Farm Bureau approves real customer information." />
      {error && <Banner tone="bad">{error}</Banner>}
      <form className="card" onSubmit={submit}>
        <div className="form-grid">
          <label className="field">Customer name<input required maxLength={200} value={f.customerName} onChange={set('customerName')} /></label>
          <label className="field">Quote type<select value={f.type} onChange={set('type')}>{QUOTE_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
          <label className="field">Phone<input type="tel" maxLength={40} value={f.phone} onChange={set('phone')} /></label>
          <label className="field">Email<input type="email" maxLength={320} value={f.email} onChange={set('email')} /></label>
          <label className="field full">Address<input maxLength={400} value={f.address} onChange={set('address')} /></label>
          <label className="field full">Current insurance<input maxLength={200} value={f.currentInsurance} onChange={set('currentInsurance')} /></label>
          <label className="field full">Needs and notes<textarea maxLength={4000} value={f.notes} onChange={set('notes')} /></label>
          <label className="field full">Documents (optional · PDF, images, Word · 5 MB each · up to 10)
            <input type="file" multiple accept={ACCEPT} onChange={(e) => { const list = [...(e.target.files ?? [])]; setFiles(list); setError(checkFiles(list) ?? ''); }} />
          </label>
        </div>
        <div className="actions">
          <Link className="btn" to={`${a.base}/intake`}>Cancel</Link>
          <button className="btn primary" type="submit" disabled={busy}>{busy ? 'Submitting…' : 'Submit request'}</button>
        </div>
      </form>
    </>
  );
}

export function IntakeAssign() {
  const a = useAgency();
  const { intakeId = '' } = useParams();
  const api = useApiClient();
  const nav = useNavigate();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const q = useQuery<{ intake: IntakeItem[] }>(a.path('/intake'));
  const item = q.data?.intake.find((i) => i.intakeId === intakeId);
  const staff = a.team.filter((m) => m.status !== 'Disabled');
  const [owner, setOwner] = useState('');
  const [due, setDue] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function assign(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const o = await api<{ opportunityId: string }>(a.path(`/intake/${intakeId}/assign`), { method: 'POST', body: { ownerMembershipId: owner, dueDate: due || null, notes } });
      refreshCounts();
      a.notify('ok', `Assigned to ${a.name(owner)} and moved to Quotes.`);
      nav(`${a.base}/quotes/${o.opportunityId}`);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  if (!a.manager) return <Banner tone="warn">Only the agency owner or office manager can assign requests.</Banner>;
  if (q.error) return <Banner tone="bad">{q.error.message}</Banner>;
  if (!q.data) return <p className="muted">Loading…</p>;
  if (!item) return <Banner tone="warn">This request was not found.</Banner>;
  if (item.stage !== 'Unassigned') return <Banner tone="warn">This request is already assigned. <Link to={`${a.base}/quotes/${item.opportunityId}`}>Open the quote</Link></Banner>;
  return (
    <>
      <div className="crumbs"><Link to={`${a.base}/intake`}>Quote intake</Link> / {item.customerName}</div>
      <PageHead title={`Assign ${item.customerName}`} sub={`${item.type} · submitted ${dateTime(item.submittedAt)} by ${a.name(item.submittedBy)}`} />
      {error && <Banner tone="bad">{error}</Banner>}
      <div className="grid2">
        <section className="card">
          <h2>Request</h2>
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', fontSize: 14 }}>
            <dt className="muted">Phone</dt><dd style={{ margin: 0 }}>{item.phone ?? '—'}</dd>
            <dt className="muted">Email</dt><dd style={{ margin: 0 }}>{item.email ?? '—'}</dd>
            <dt className="muted">Address</dt><dd style={{ margin: 0 }}>{item.address ?? '—'}</dd>
            <dt className="muted">Current insurance</dt><dd style={{ margin: 0 }}>{item.currentInsurance ?? '—'}</dd>
            <dt className="muted">Notes</dt><dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{item.notes ?? '—'}</dd>
          </dl>
        </section>
        <form className="card" onSubmit={assign}>
          <h2>Assignment</h2>
          <div className="form-grid">
            <label className="field full">Assign to
              <select required value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">Select a team member…</option>
                {staff.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email}{m.status === 'Invited' ? ' (invited)' : ''}</option>)}
              </select>
            </label>
            <label className="field full">Due date<input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></label>
            <label className="field full">Notes for the assignee<textarea value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} /></label>
          </div>
          <div className="actions"><button className="btn primary" type="submit" disabled={busy || !owner}>{busy ? 'Assigning…' : 'Assign and create quote'}</button></div>
        </form>
      </div>
      <div style={{ marginTop: 16 }}>
        <DocumentsPanel path={`/intake/${item.intakeId}/documents`} />
        <p className="muted" style={{ fontSize: 13 }}>Documents move to the quote when this request is assigned.</p>
      </div>
    </>
  );
}
